
import { ChevronRightIcon } from '@chakra-ui/icons';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Select,
  Skeleton,
  Spinner,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import MoreIcon from '../../../assets/icons/more-horizontal-icon.svg';

import { usePermissions } from '../../../hooks/usePermissions';
import { getMarketDetails } from '../../../services/markets/getMarketDetails';
import { getMarketList } from '../../../services/markets/getMarketList';

import { getPracticesList } from '../../../services/practices/getPractices';
import { updatePracticeById } from '../../../services/practices/updatePracticeById';
import { postUnifiedSearch } from '../../../services/unified-search/unified-search';

import CustomFieldBadge from '../../../components/customfield-badge/CustomFieldBadge';
import { Practice } from '../../../types/practices/types';
import { getCustomFieldValue } from '../../../utils/customFieldUtils';

interface MarketOption {
  label: string;
  value: string;
}

interface SearchResult {
  id: string;
  name: string;
  address1: string;
  department: string;
  athenaId: string;
  taxId: string;
  tablespace: string;
  treatment: string;
  isInMarket: boolean;
}

// Helper functions
const todayISO = () => new Date().toISOString().slice(0, 10);

const patchMarket = async (id: string, marketId: string | null) => {
  const payload =
    marketId === null
      ? { market: null }
      : { market: marketId, startDate: todayISO() };
  const result = await updatePracticeById(id, payload);
  return result;
};

// Concurrency limiter
const limit = (n: number) => {
  let active = 0;
  const queue: (() => Promise<unknown>)[] = [];
  const run = () => {
    if (active >= n || !queue.length) return;
    active++;
    queue.shift()!().finally(() => {
      active--;
      run();
    });
    run();
  };
  return <T,>(f: () => Promise<T>) =>
    new Promise<T>((res, rej) => {
      queue.push(() => f().then(res, rej));
      run();
    });
};

const limit3 = limit(3);

const MarketPracticeManagement = () => {
  const { id: marketId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { isAdmin, canEditInsurance, permissions } = usePermissions();

  // State management
  const [marketData, setMarketData] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [marketLoading, setMarketLoading] = useState(false);
  const [isFullyLoading, setIsFullyLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>(
    []
  );
  const [searchLoading, setSearchLoading] = useState(false);
  const [marketPractices, setMarketPractices] = useState<Practice[]>(
    []
  );
  const [practicesLoading, setPracticesLoading] = useState(false);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());

  // Multi-select state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    new Set()
  );
  const [bulkBusy, setBulkBusy] = useState<null | 'add' | 'remove'>(
    null
  );
  const [bulkProgress, setBulkProgress] = useState<{
    completed: number;
    total: number;
    current: string;
  } | null>(null);

  // Modal states
  const removeModal = useDisclosure();
  const bulkRemoveModal = useDisclosure();
  const [selectedPractice, setSelectedPractice] =
    useState<SearchResult | null>(null);
  const [selectedMarket, setSelectedMarket] = useState('');
  const [bulkRemoveTargetMarket, setBulkRemoveTargetMarket] =
    useState('');
  const [marketOptions, setMarketOptions] = useState<MarketOption[]>(
    []
  );
  const [expandedAccordion, setExpandedAccordion] =
    useState<string>('');
  const [bulkRemoveCount, setBulkRemoveCount] = useState(0);

  // Refs for stale request protection
  const marketPracticesRef = useRef<Practice[]>([]);
  const searchControllerRef = useRef<AbortController | null>(null);
  const searchRequestIdRef = useRef(0);

  // withLock wrapper for serialized updates
  const withLock = useCallback(
    async (id: string, fn: () => Promise<void>) => {
      if (busyIds.has(id)) return;

      setBusyIds((prev) => new Set(prev).add(id));
      try {
        await fn();
      } finally {
        setBusyIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    [busyIds]
  );

  // Fetch market data
  const fetchMarketData = useCallback(async () => {
    if (!marketId) return;

    setMarketLoading(true);
    try {
      // Try getMarketDetails first
      const { data } = await getMarketDetails(marketId);
      let marketData: Record<string, unknown> | null = null;

      if (data?.market && Object.keys(data.market).length > 0) {
        // Use getMarketDetails response
        marketData = data.market as unknown as Record<
          string,
          unknown
        >;
      } else {
        // Fallback to getMarketList
        const listResponse = await getMarketList([marketId]);

        if (listResponse.data?.states) {
          // Find the market in the states array
          for (const state of listResponse.data.states) {
            const foundMarket = state.markets.find(
              (market) => market.id === marketId
            );
            if (foundMarket) {
              marketData = foundMarket as unknown as Record<
                string,
                unknown
              >;
              break;
            }
          }
        }
      }

      if (marketData) {
        setMarketData(marketData);
      } else {
        throw new Error('Market not found');
      }
    } catch (error) {
      toast({
        title: 'Error loading market',
        description: 'Failed to load market data. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setMarketLoading(false);
      setIsFullyLoading(false);
    }
  }, [marketId, toast]);

  // Fetch market practices
  const fetchMarketPractices = useCallback(async () => {
    if (!marketId) return;

    setPracticesLoading(true);
    try {
      // Try multiple filter approaches to ensure we get all practices
      let allPractices: Practice[] = [];

      // Approach 1: Use marketIds filter
      try {
        const { data: data1 } = await getPracticesList(
          null,
          {
            marketIds: marketId,
          },
          10
        );
        if (data1?.practices) {
          allPractices = [...allPractices, ...data1.practices];
        }
      } catch (error) {
        // Silently handle marketIds filter error
      }

      // Approach 2: Use Market filter (mapped to marketIds)
      try {
        const { data: data2 } = await getPracticesList(
          null,
          {
            Market: marketId,
          },
          10
        );
        if (data2?.practices) {
          // Merge without duplicates
          const existingIds = new Set(allPractices.map((p) => p.id));
          const newPractices = data2.practices.filter(
            (p) => !existingIds.has(p.id)
          );
          allPractices = [...allPractices, ...newPractices];
        }
      } catch (error) {
        // Silently handle Market filter error
      }

      // Remove duplicates based on ID
      const uniquePractices = allPractices.filter(
        (practice, index, self) =>
          index === self.findIndex((p) => p.id === practice.id)
      );

      setMarketPractices(uniquePractices);
    } catch (error) {
      // Handle error silently
      toast({
        title: 'Error loading practices',
        description:
          'Failed to load market practices. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setPracticesLoading(false);
    }
  }, [marketId, toast]);

  // Load market options from API (fresh IDs)
  const loadMarketOptions = useCallback(async () => {
    try {
      const { data } = await getMarketList();
      if (data?.states) {
        const options: MarketOption[] = data.states
          .reduce(
            (
              acc: MarketOption[],
              state: { markets: { id: string; name: string }[] }
            ) => {
              return acc.concat(
                state.markets.map(
                  (market: { id: string; name: string }) => ({
                    label: market.name,
                    value: market.id,
                  })
                )
              );
            },
            []
          )
          .sort((a, b) => a.label.localeCompare(b.label));

        setMarketOptions(options);
      }
    } catch (error) {
      toast({
        title: 'Warning',
        description:
          'Failed to load market options. Some features may not work correctly.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [toast]);

  // Search practices with stale request protection
  const searchPractices = useCallback(async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    // Cancel previous request
    if (searchControllerRef.current) {
      searchControllerRef.current.abort();
    }

    // Create new controller and request ID
    const controller = new AbortController();
    searchControllerRef.current = controller;
    const requestId = ++searchRequestIdRef.current;

    setSearchLoading(true);
    try {
      const { data } = await postUnifiedSearch(searchQuery, 50, undefined, undefined, permissions.isAdmin);

      // Check if this is still the latest request
      if (requestId !== searchRequestIdRef.current) {
        return;
      }

      const practices = data.practices || [];
      const marketPracticeIds = new Set(
        marketPractices.map((p) => p.id)
      );

      // Transform search results and check if they're in current market
      const transformedResults: SearchResult[] = practices
        .filter((practice: Practice) => {
          // Filter out practices that are already in the market
          const isInMarket = marketPracticeIds.has(practice.id);
          return !isInMarket; // Only show practices that are NOT in the market
        })
        .map((practice: Practice) => ({
          id: practice.id,
          name: practice.name,
          address1: practice.address1 || '',
          department: '',
          athenaId: '',
          taxId:
            practice.customFields?.['Group-TID']?.[0]?.value || '',
          tablespace:
            practice.customFields?.['Group-TID']?.[0]?.value || '',
          treatment: 'All ac', // Default value as shown in UI
          isInMarket: false, // Since we filtered out market practices, this is always false
        }));

      setSearchResults(transformedResults);

      // Show notification based on results
      if (transformedResults.length > 0) {
        toast({
          title: 'Search Results Found!',
          description: `Found ${transformedResults.length} practice${
            transformedResults.length !== 1 ? 's' : ''
          } matching "${searchQuery}".`,
          status: 'success',
          duration: 4000,
          isClosable: true,
          position: 'top-right',
        });
      } else {
        toast({
          title: 'No Results Found',
          description: `No practices found matching "${searchQuery}". Try adjusting your search terms.`,
          status: 'info',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      toast({
        title: 'Search failed',
        description: 'Failed to search practices. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      if (requestId === searchRequestIdRef.current) {
        setSearchLoading(false);
      }
    }
  }, [searchQuery, marketPractices, toast, permissions.isAdmin]);

  // Multi-select handlers
  const toggleSelection = useCallback(
    (id: string) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }

        // Show notification when selecting practices for bulk add
        if (next.size > 0 && next.size <= 5) {
          const practice =
            searchResults.find((p) => p.id === id) ||
            marketPractices.find((p) => p.id === id);
          if (practice) {
            toast({
              title: `${next.has(id) ? 'Selected' : 'Deselected'} ${
                practice.name
              }`,
              description: `${next.size} practice${
                next.size !== 1 ? 's' : ''
              } selected for bulk action`,
              status: 'info',
              duration: 1500,
              isClosable: true,
            });
          }
        }

        return next;
      });
    },
    [searchResults, marketPractices, toast]
  );

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // Remove practice from market (minimal patch)
  const removePracticeFromMarket = useCallback(async () => {
    if (!selectedPractice || !selectedMarket) return;

    const targetMarketId = selectedMarket;

    await withLock(selectedPractice.id, async () => {
      // Optimistic update: Remove practice from current market list immediately
      setMarketPractices((prev) =>
        prev.filter((p) => p.id !== selectedPractice.id)
      );

      try {
        await patchMarket(selectedPractice.id, targetMarketId);

        toast({
          title: 'Practice moved successfully!',
          status: 'success',
          duration: 2500,
          isClosable: true,
        });

        // Update search results to reflect the new market status
        setSearchResults((prev) =>
          prev.map((practice) => ({
            ...practice,
            isInMarket:
              practice.id === selectedPractice.id
                ? false
                : practice.isInMarket,
          }))
        );

        // Refresh market practices from server to ensure accuracy
        // Add a small delay to allow API to propagate changes
        await new Promise((resolve) => setTimeout(resolve, 500));
        await fetchMarketPractices();

        // Show notification about Step 2 update
        setTimeout(() => {
          toast({
            title: 'Step 2 Updated!',
            description: `${selectedPractice.name} removed from Current Market Practices.`,
            status: 'info',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }, 1000);

        // Close modal
        removeModal.onClose();
        setSelectedPractice(null);
        setSelectedMarket('');
      } catch (error) {
        // Revert optimistic update on failure
        const practiceToRestore = {
          id: selectedPractice.id,
          name: selectedPractice.name,
          address1: selectedPractice.address1,
          clientId: '',
          address2: null,
          city: null,
          state: null,
          zip: null,
          timezone: null,
          observesDaylightSavingsTime: false,
          startDate: null,
          endDate: null,
          active: true,
          providerCount: 0,
          customFields: {},
          managerEmail: null,
          managerName: null,
          managerPhone: null,
          market: marketId,
          marketId: marketId,
        } as Practice;
        setMarketPractices((prev) => [...prev, practiceToRestore]);

        toast({
          title: 'Failed to remove practice',
          description:
            'The operation failed. Please check your connection and try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    });
  }, [
    selectedPractice,
    selectedMarket,
    toast,
    removeModal,
    withLock,
    fetchMarketPractices,
    marketId,
  ]);

  // Handle remove button click
  const handleRemoveClick = (practice: SearchResult) => {
    setSelectedPractice(practice);
    setSelectedMarket('');
    removeModal.onOpen();
  };

  // Bulk operations
  const bulkAdd = useCallback(
    async (ids: string[]) => {
      if (!isAdmin()) {
        toast({
          title: 'Access Denied',
          description:
            'You do not have permission to edit practices.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      setBulkBusy('add');
      setBulkProgress({
        completed: 0,
        total: ids.length,
        current: '',
      });

      // Show initial notification
      toast({
        title: 'Adding practices to market...',
        description: `Starting to add ${ids.length} practice${
          ids.length !== 1 ? 's' : ''
        } to the market.`,
        status: 'info',
        duration: 2000,
        isClosable: true,
      });

      // Optimistic update: Add practices to current market list immediately
      const practicesToAdd = searchResults
        .filter((p) => ids.includes(p.id))
        .map(
          (searchResult) =>
            ({
              id: searchResult.id,
              name: searchResult.name,
              address1: searchResult.address1,
              clientId: '',
              address2: null,
              city: null,
              state: null,
              zip: null,
              timezone: null,
              observesDaylightSavingsTime: false,
              startDate: null,
              endDate: null,
              active: true,
              providerCount: 0,
              customFields: {},
              managerEmail: null,
              managerName: null,
              managerPhone: null,
              market: marketId,
              marketId: marketId,
            } as Practice)
        );

      setMarketPractices((prev) => {
        const updated = [...prev, ...practicesToAdd];
        return updated;
      });

      try {
        const results = [];
        for (let i = 0; i < ids.length; i++) {
          const id = ids[i];
          const practice = searchResults.find((p) => p.id === id);
          const practiceName = practice?.name || `Practice ${i + 1}`;

          setBulkProgress({
            completed: i,
            total: ids.length,
            current: practiceName,
          });

          try {
            const result = await limit3(() =>
              patchMarket(id, marketId!)
            );
            results.push({ status: 'fulfilled', value: result });
          } catch (error) {
            results.push({ status: 'rejected', reason: error });
          }
        }

        setBulkProgress({
          completed: ids.length,
          total: ids.length,
          current: 'Complete',
        });

        const failures = results.filter(
          (r) => r.status === 'rejected'
        );
        const successes = results.filter(
          (r) => r.status === 'fulfilled'
        );

        if (failures.length > 0) {
          // Revert optimistic update for failed practices
          const failedIds = failures.map((_, index) => ids[index]);

          setMarketPractices((prev) => {
            const reverted = prev.filter(
              (p) => !failedIds.includes(p.id)
            );
            return reverted;
          });

          toast({
            title: 'Some practices failed to add',
            description: `${successes.length} of ${ids.length} practices added successfully. ${failures.length} failed.`,
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
        } else {
          toast({
            title: 'All practices added successfully!',
            description: `${successes.length} practice${
              successes.length !== 1 ? 's' : ''
            } added to the market.`,
            status: 'success',
            duration: 3000,
            isClosable: true,
          });

          // Refresh market practices from server to ensure accuracy
          // Add a delay to allow API to propagate changes
          await new Promise((resolve) => setTimeout(resolve, 2000));
          await fetchMarketPractices();

          // Show notification about Step 2 update and auto-expand
          setTimeout(() => {
            toast({
              title: 'Step 2 Updated!',
              description: `${successes.length} practice${
                successes.length !== 1 ? 's' : ''
              } added to Current Market Practices. Click to view.`,
              status: 'info',
              duration: 4000,
              isClosable: true,
              position: 'top-right',
            });

            // Auto-expand Step 2 accordion to show newly added practices
            setExpandedAccordion('current-practices');
          }, 1000);
        }

        // Update search results to reflect the new market status
        setSearchResults((prev) =>
          prev.map((practice) => ({
            ...practice,
            isInMarket: ids.includes(practice.id)
              ? true
              : practice.isInMarket,
          }))
        );

        clearSelection();
      } catch (error) {
        // Revert optimistic update on complete failure
        setMarketPractices((prev) => {
          const reverted = prev.filter((p) => !ids.includes(p.id));
          return reverted;
        });

        toast({
          title: 'Bulk add failed',
          description:
            'Failed to add practices. All changes have been reverted. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setBulkBusy(null);
        setBulkProgress(null);
      }
    },
    [
      isAdmin,
      toast,
      marketId,
      clearSelection,
      searchResults,
      fetchMarketPractices,
    ]
  );

  const bulkRemove = useCallback(
    async (ids: string[], targetMarketId: string | null) => {
      if (!isAdmin()) {
        toast({
          title: 'Access Denied',
          description:
            'You do not have permission to edit practices.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      setBulkBusy('remove');
      setBulkProgress({
        completed: 0,
        total: ids.length,
        current: '',
      });

      // Store practices being removed for potential rollback
      const practicesBeingRemoved = marketPractices.filter((p) =>
        ids.includes(p.id)
      );

      // Optimistic update: Remove practices from current market list immediately
      setMarketPractices((prev) =>
        prev.filter((p) => !ids.includes(p.id))
      );

      try {
        const results = [];
        for (let i = 0; i < ids.length; i++) {
          const id = ids[i];
          const practice = practicesBeingRemoved.find(
            (p) => p.id === id
          );
          setBulkProgress({
            completed: i,
            total: ids.length,
            current: practice?.name || `Practice ${i + 1}`,
          });

          try {
            const result = await limit3(() =>
              patchMarket(id, targetMarketId)
            );
            results.push({ status: 'fulfilled', value: result });
          } catch (error) {
            results.push({ status: 'rejected', reason: error });
          }
        }

        setBulkProgress({
          completed: ids.length,
          total: ids.length,
          current: 'Complete',
        });

        const failures = results.filter(
          (r) => r.status === 'rejected'
        );

        if (failures.length > 0) {
          // Revert optimistic update for failed practices
          const failedIds = failures.map((_, index) => ids[index]);
          const failedPractices = practicesBeingRemoved.filter((p) =>
            failedIds.includes(p.id)
          );
          setMarketPractices((prev) => [...prev, ...failedPractices]);

          toast({
            title: 'Some practices failed to remove',
            description: `${failures.length} of ${ids.length} practices failed to remove.`,
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
        } else {
          toast({
            title: 'All practices moved successfully!',
            status: 'success',
            duration: 2500,
            isClosable: true,
          });

          // Refresh market practices from server to ensure accuracy
          // Add a small delay to allow API to propagate changes
          await new Promise((resolve) => setTimeout(resolve, 500));
          await fetchMarketPractices();

          // Show notification about Step 2 update if practices were removed
          if (ids.length > 0) {
            setTimeout(() => {
              toast({
                title: 'Step 2 Updated!',
                description: `${ids.length} practice${
                  ids.length !== 1 ? 's' : ''
                } removed from Current Market Practices.`,
                status: 'info',
                duration: 3000,
                isClosable: true,
                position: 'top-right',
              });
            }, 1000);
          }
        }

        // Update search results to reflect the new market status
        setSearchResults((prev) =>
          prev.map((practice) => ({
            ...practice,
            isInMarket: ids.includes(practice.id)
              ? false
              : practice.isInMarket,
          }))
        );

        clearSelection();
      } catch (error) {
        // Revert optimistic update on complete failure
        setMarketPractices((prev) => [
          ...prev,
          ...practicesBeingRemoved,
        ]);

        toast({
          title: 'Bulk remove failed',
          description:
            'Failed to remove practices. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setBulkBusy(null);
        setBulkProgress(null);
      }
    },
    [
      isAdmin,
      toast,
      clearSelection,
      marketPractices,
      fetchMarketPractices,
    ]
  );

  const handleBulkRemove = useCallback(async () => {
    const marketIds = Array.from(selectedIds).filter((id) =>
      marketPractices.some((p) => p.id === id)
    );

    if (marketIds.length > 0 && bulkRemoveTargetMarket) {
      await bulkRemove(marketIds, bulkRemoveTargetMarket);
      bulkRemoveModal.onClose();
      setBulkRemoveTargetMarket('');
      setBulkRemoveCount(0);
    }
  }, [
    selectedIds,
    marketPractices,
    bulkRemoveTargetMarket,
    bulkRemove,
    bulkRemoveModal,
  ]);

  // Handle market termination
  const handleTerminateMarket = useCallback(async () => {
    if (!marketId || !isAdmin() || !marketData) return;

    try {
      // Import updateMarket function
      const { updateMarket } = await import(
        '../../../services/markets/updateMarket'
      );

      // Create the full update payload with current market data
      const updatePayload = {
        id: marketId,
        name: marketData.name as string,
        location: marketData.location as string,
        nickname: marketData.nickname as string,
        tablespace: marketData.tablespace as string,
        taxid: marketData.taxid as string,
        pronunciation: marketData.pronunciation as string,
        resource: marketData.resource as Record<string, string>,
        active: false, // Set to false to terminate
        external: (marketData.external as boolean) ?? false,
      };

      await updateMarket(updatePayload);

      toast({
        title: 'Market Terminated Successfully',
        description: `The market "${marketData.name}" has been terminated and is now invisible to users.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Redirect to markets list
      setTimeout(() => {
        navigate('/markets');
      }, 2000);
    } catch (error) {
      // Handle error silently

      // Handle specific error case
      if (
        error instanceof Error &&
        error.message.includes(
          'Failed to retrieve the markets from DB'
        )
      ) {
        // Treat this as success (backend quirk)
        toast({
          title: 'Market Terminated Successfully',
          description: `The market "${marketData.name}" has been terminated and is now invisible to users.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        setTimeout(() => {
          navigate('/markets');
        }, 2000);
      } else {
        toast({
          title: 'Failed to Terminate Market',
          description:
            'An error occurred while terminating the market. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  }, [marketId, isAdmin, marketData, toast, navigate]);

  // Effects
  useEffect(() => {
    fetchMarketData();
    fetchMarketPractices();
    loadMarketOptions();
  }, [fetchMarketData, fetchMarketPractices, loadMarketOptions]);

  // Sync marketPractices ref
  useEffect(() => {
    marketPracticesRef.current = marketPractices;
  }, [marketPractices]);

  // Auto-expand search results accordion when results are available (only after actual search)
  useEffect(() => {
    if (searchResults.length > 0 && searchQuery.trim()) {
      setExpandedAccordion('search-results');
    }
  }, [searchResults, searchQuery]);

  // Debounced search helper (optional - not used in current implementation)
  // const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

  // const debouncedSearch = useCallback(() => {
  //   if (searchTimeout) {
  //     clearTimeout(searchTimeout);
  //   }

  //   const timeout = setTimeout(() => {
  //     searchPractices();
  //   }, 350);

  //   setSearchTimeout(timeout);
  // }, [searchPractices, searchTimeout]);

  // Handle enter key
  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        searchPractices();
      }
    },
    [searchPractices]
  );

  if (marketLoading) {
    return (
      <Flex justify="center" align="center" height="50vh">
        <Spinner size="lg" />
      </Flex>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          Manage Practices -{' '}
          {(marketData?.name as string) || 'Market'} - CHS
        </title>
      </Helmet>

      <Breadcrumb
        mt="2rem"
        ml="2rem"
        mr="2rem"
        mb="1.62rem"
        separator={<ChevronRightIcon color="gray.500" />}
      >
        <BreadcrumbItem>
          <BreadcrumbLink
            onClick={() => {
              navigate('/');
            }}
          >
            <Text
              fontSize="0.875rem"
              fontWeight="400"
              lineHeight="1.25rem"
              color="gray.700"
            >
              Home
            </Text>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <Skeleton isLoaded={!isFullyLoading}>
          <BreadcrumbItem>
            <Flex alignItems="center">
              <BreadcrumbLink
                onClick={() => {
                  navigate(`/states/${marketData?.state}`);
                }}
              >
                <Text
                  fontSize="0.875rem"
                  fontWeight="400"
                  lineHeight="1.25rem"
                  color="gray.700"
                >
                  {marketData?.state as string}
                </Text>
              </BreadcrumbLink>
              <ChevronRightIcon color="gray.500" ml="0.5rem" />
            </Flex>
          </BreadcrumbItem>
        </Skeleton>

        <Skeleton isLoaded={!isFullyLoading}>
          <BreadcrumbItem>
            <Flex alignItems="center">
              <BreadcrumbLink
                onClick={() => {
                  navigate(`/markets/${marketId}`);
                }}
              >
                <Text
                  fontSize="0.875rem"
                  fontWeight="400"
                  lineHeight="1.25rem"
                  color="gray.700"
                >
                  {marketData?.name as string}
                </Text>
              </BreadcrumbLink>
              <ChevronRightIcon color="gray.500" ml="0.5rem" />
            </Flex>
          </BreadcrumbItem>
        </Skeleton>

        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink href="#">
            <Text
              fontSize="0.875rem"
              fontWeight="400"
              lineHeight="1.25rem"
              color="gray.700"
            >
              Practice Management
            </Text>
          </BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <Box ml="11.25rem" mr="11.25rem" mb="3rem" mt="1.75rem">
        <Flex
          direction="column"
          align="flex-start"
          gap="1rem"
          width="100%"
        >
          {/* Market Header with Three Dots Menu */}
          <Flex
            justify="space-between"
            align="center"
            width="100%"
            mb="2rem"
          >
            <Text
              fontSize="1.875rem"
              fontWeight="700"
              lineHeight={'133%'}
              color="text.primary"
            >
              {(marketData?.name as string) || 'Market'} - Practice
              Management
            </Text>

            {(isAdmin() || canEditInsurance()) && (
              <Menu>
                <MenuButton
                  as={IconButton}
                  icon={<img src={MoreIcon} />}
                  size="sm"
                  aria-label="Options"
                  borderRadius="0.375rem"
                  background={'background.secondary'}
                  width="2.5rem"
                  height="2.5rem"
                  cursor={'pointer'}
                  border="none"
                  transition="all 0.2s ease-in-out"
                />
                <MenuList>
                  <MenuItem
                    onClick={() => navigate(`/markets/${marketId}`)}
                  >
                    Back to Market Details
                  </MenuItem>
                  {(isAdmin() || canEditInsurance()) && (
                    <MenuItem
                      onClick={() =>
                        navigate(`/markets/edit/${marketId}`)
                      }
                    >
                      Edit Market Details
                    </MenuItem>
                  )}
                  {(isAdmin() || canEditInsurance()) && (
                    <MenuItem
                      onClick={() =>
                        navigate(`/markets/${marketId}/practices`)
                      }
                    >
                      Manage Practices
                    </MenuItem>
                  )}
                  {isAdmin() && (
                    <MenuItem
                      onClick={() => {
                        // Open terminate market modal
                        const confirmTerminate = window.confirm(
                          `Are you sure you want to terminate the market "${marketData?.name}"?\n\nThis will:\n• Make the market invisible to users\n• Mark all linked practices as inactive\n• This action cannot be undone\n\nType "TERMINATE" to confirm:`
                        );

                        if (confirmTerminate) {
                          const userInput = window.prompt(
                            'Type "TERMINATE" to confirm market termination:'
                          );

                          if (userInput === 'TERMINATE') {
                            handleTerminateMarket();
                          } else {
                            toast({
                              title: 'Termination Cancelled',
                              description:
                                'Market termination was cancelled.',
                              status: 'info',
                              duration: 3000,
                              isClosable: true,
                            });
                          }
                        }
                      }}
                      color="red.600"
                      _hover={{ bg: 'red.50' }}
                    >
                      Terminate Market
                    </MenuItem>
                  )}
                </MenuList>
              </Menu>
            )}
          </Flex>

          {/* Practices Section */}
          <Box width="100%">
            <Text
              fontSize="1.25rem"
              fontWeight="700"
              color="text.secondary"
              mb="0.5rem"
            >
              Practices
            </Text>
            <Text fontSize="0.875rem" color="gray.600" mb="1rem">
              Search for practices to add to this market or manage
              existing ones
            </Text>
            <Alert status="info" borderRadius="md" mb="2rem">
              <AlertIcon />
              <Box>
                <AlertTitle fontSize="0.875rem" mb="0.5rem">
                  How to use this page:
                </AlertTitle>
                <AlertDescription fontSize="0.75rem">
                  <VStack align="start" spacing="0.25rem">
                    <Text>
                      <Badge colorScheme="blue" size="sm" mr="0.5rem">
                        1
                      </Badge>
                      <strong>Search:</strong> Use the search bar to
                      find practices by name, address, or phone number
                    </Text>
                    <Text>
                      <Badge colorScheme="blue" size="sm" mr="0.5rem">
                        2
                      </Badge>
                      <strong>Select:</strong> Check the boxes next to
                      practices you want to add or remove
                    </Text>
                    <Text>
                      <Badge colorScheme="blue" size="sm" mr="0.5rem">
                        3
                      </Badge>
                      <strong>Bulk Actions:</strong> Use the toolbar
                      at the bottom to add/remove multiple practices
                      at once
                    </Text>
                    <Text>
                      <Badge colorScheme="blue" size="sm" mr="0.5rem">
                        4
                      </Badge>
                      <strong>Individual Actions:</strong> Click on
                      practice cards to view details, or use the
                      "Edit" button
                    </Text>
                  </VStack>
                </AlertDescription>
              </Box>
            </Alert>

            {/* Search Section */}
            <Box
              mb="2rem"
              p="1.5rem"
              bg="white"
              borderRadius="lg"
              border="2px solid"
              borderColor="blue.100"
              boxShadow="sm"
            >
              <HStack spacing="1rem" mb="1rem">
                <Badge
                  colorScheme="blue"
                  variant="subtle"
                  fontSize="0.75rem"
                >
                  Step 1
                </Badge>
                <Text
                  fontSize="0.875rem"
                  fontWeight="600"
                  color="gray.700"
                >
                  Search Practices
                </Text>
              </HStack>

              {isAdmin() || canEditInsurance() ? (
                <>
                  <HStack spacing="1rem" mb="0.5rem">
                    <Input
                      placeholder="Enter practice name, address, or phone number..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={handleKeyPress}
                      width="400px"
                      bg="white"
                      border="2px solid"
                      borderColor={
                        searchQuery ? 'blue.300' : 'gray.200'
                      }
                      _focus={{
                        borderColor: 'blue.500',
                        boxShadow:
                          '0 0 0 3px rgba(66, 153, 225, 0.1)',
                      }}
                      _hover={{
                        borderColor: 'blue.300',
                      }}
                      transition="all 0.2s ease-in-out"
                    />
                    <Button
                      colorScheme="blue"
                      size="md"
                      onClick={searchPractices}
                      isLoading={searchLoading}
                      loadingText="Searching..."
                      isDisabled={!searchQuery.trim()}
                      _hover={{ transform: 'translateY(-1px)' }}
                      transition="all 0.2s ease-in-out"
                    >
                      {searchLoading ? 'Searching...' : 'Search'}
                    </Button>
                    {searchQuery && (
                      <Button
                        variant="outline"
                        size="md"
                        onClick={() => {
                          setSearchQuery('');
                          setSearchResults([]);
                          setExpandedAccordion('');
                          toast({
                            title: 'Search Cleared',
                            description:
                              'Search query and results have been cleared.',
                            status: 'info',
                            duration: 2000,
                            isClosable: true,
                            position: 'top-right',
                          });
                        }}
                        _hover={{ bg: 'gray.100' }}
                        transition="all 0.2s ease-in-out"
                      >
                        Clear
                      </Button>
                    )}
                  </HStack>

                  <HStack spacing="0.5rem" align="center">
                    <Text fontSize="0.75rem" color="gray.500">
                      Tip: Press Enter to search, or click the Search
                      button
                    </Text>
                    {searchResults.length > 0 && (
                      <Badge
                        colorScheme="green"
                        variant="subtle"
                        fontSize="0.7rem"
                      >
                        {searchResults.length} found
                      </Badge>
                    )}
                  </HStack>
                </>
              ) : (
                <Box
                  p="1rem"
                  bg="gray.50"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="gray.200"
                >
                  <Text
                    fontSize="0.875rem"
                    color="gray.600"
                    textAlign="center"
                  >
                    You don't have permission to search and manage
                    practices. Contact your administrator for access.
                  </Text>
                </Box>
              )}
            </Box>

            {/* Market Practices Table */}
            <Box mb="2rem">
              <Accordion
                allowToggle
                index={
                  expandedAccordion === 'current-practices' ? 0 : -1
                }
                onChange={(index) =>
                  setExpandedAccordion(
                    index === 0 ? 'current-practices' : ''
                  )
                }
              >
                <AccordionItem
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                >
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      <HStack spacing="1rem">
                        <Badge
                          colorScheme="orange"
                          variant="subtle"
                          fontSize="0.75rem"
                        >
                          Step 2
                        </Badge>
                        <Text fontSize="1rem" fontWeight="600">
                          Current Market Practices
                        </Text>
                        <Badge
                          colorScheme={
                            marketPractices.length > 0
                              ? 'green'
                              : 'gray'
                          }
                          variant="solid"
                          fontSize="0.7rem"
                        >
                          {marketPractices.length} practice
                          {marketPractices.length !== 1 ? 's' : ''}
                        </Badge>
                        {marketPractices.length > 0 && (
                          <Badge
                            colorScheme="orange"
                            variant="outline"
                            fontSize="0.65rem"
                            animation="pulse"
                          >
                            Click to view
                          </Badge>
                        )}
                      </HStack>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    <HStack
                      spacing="1rem"
                      mb="1rem"
                      justify="flex-end"
                    >
                      <Text fontSize="0.875rem" color="gray.500">
                        {practicesLoading
                          ? 'Loading...'
                          : `Auto-updated • ${
                              marketPractices.length
                            } practice${
                              marketPractices.length !== 1 ? 's' : ''
                            }`}
                      </Text>
                    </HStack>

                    {marketPractices.length > 0 && (
                      <Alert
                        status="warning"
                        borderRadius="md"
                        mb="1rem"
                      >
                        <AlertIcon />
                        <Box>
                          <AlertTitle fontSize="0.75rem" mb="0.25rem">
                            Ready to remove practices?
                          </AlertTitle>
                          <AlertDescription fontSize="0.7rem">
                            Select practices using the checkboxes,
                            then use the toolbar at the bottom to
                            remove them from this market
                          </AlertDescription>
                        </Box>
                      </Alert>
                    )}
                    {practicesLoading ? (
                      <Flex
                        justify="center"
                        align="center"
                        py="3rem"
                        bg="gray.50"
                        borderRadius="md"
                        border="1px solid"
                        borderColor="gray.200"
                      >
                        <VStack spacing="1rem">
                          <Spinner
                            size="lg"
                            color="blue.500"
                            thickness="4px"
                          />
                          <Text color="gray.600" fontSize="0.875rem">
                            Loading market practices...
                          </Text>
                        </VStack>
                      </Flex>
                    ) : marketPractices.length === 0 ? (
                      <Flex
                        justify="center"
                        align="center"
                        py="3rem"
                        bg="gray.50"
                        borderRadius="md"
                        border="1px solid"
                        borderColor="gray.200"
                      >
                        <VStack spacing="1rem">
                          <Text
                            color="gray.500"
                            fontSize="1rem"
                            fontWeight="500"
                          >
                            No practices in this market yet
                          </Text>
                          <Text
                            color="gray.400"
                            fontSize="0.875rem"
                            textAlign="center"
                          >
                            {isAdmin() || canEditInsurance()
                              ? 'Use the search above to find and add practices to this market'
                              : 'Contact your administrator to manage practices under this market'}
                          </Text>
                        </VStack>
                      </Flex>
                    ) : (
                      <VStack spacing="1rem" align="stretch">
                        {marketPractices.map((practice) => (
                          <HStack
                            key={practice.id}
                            spacing="1rem"
                            align="start"
                          >
                            {/* Checkbox - Outside the card */}
                            {(isAdmin() || canEditInsurance()) && (
                              <Checkbox
                                isChecked={selectedIds.has(
                                  practice.id
                                )}
                                onChange={() =>
                                  toggleSelection(practice.id)
                                }
                                colorScheme="blue"
                                mt="0.5rem"
                              />
                            )}

                            {/* Practice Card */}
                            <Box
                              flex="1"
                              p="1.5rem"
                              bg="white"
                              border="1px solid"
                              borderColor="gray.200"
                              borderRadius="0.75rem"
                              _hover={{
                                transform: 'translateY(-2px)',
                                boxShadow: 'lg',
                                borderColor: 'blue.200',
                              }}
                              transition="all 0.2s ease-in-out"
                              cursor="pointer"
                              onClick={() =>
                                navigate(`/practices/${practice.id}`)
                              }
                            >
                              {/* Action Buttons - Top Right */}
                              {(isAdmin() || canEditInsurance()) && (
                                <HStack
                                  spacing="0.75rem"
                                  justify="flex-end"
                                  position="absolute"
                                  top="1rem"
                                  right="1rem"
                                  zIndex={1}
                                >
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    colorScheme="blue"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      window.open(
                                        `/practices/edit/${practice.id}`,
                                        '_blank'
                                      );
                                    }}
                                    _hover={{
                                      bg: 'blue.50',
                                      transform: 'translateY(-1px)',
                                    }}
                                    transition="all 0.2s ease-in-out"
                                    bg="white"
                                    borderColor="blue.200"
                                  >
                                    Edit
                                  </Button>
                                  <IconButton
                                    aria-label="Remove practice from market"
                                    icon={
                                      <Text fontSize="1rem">−</Text>
                                    }
                                    size="sm"
                                    colorScheme="red"
                                    variant="solid"
                                    bg="red.500"
                                    color="white"
                                    isLoading={busyIds.has(
                                      practice.id
                                    )}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveClick({
                                        id: practice.id,
                                        name: practice.name,
                                        address1:
                                          practice.address1 || '',
                                        department: '',
                                        athenaId: '',
                                        taxId:
                                          practice.customFields?.[
                                            'Group-TID'
                                          ]?.[0]?.value || '',
                                        tablespace:
                                          practice.customFields?.[
                                            'Group-TID'
                                          ]?.[0]?.value || '',
                                        treatment: 'All ac',
                                        isInMarket: true,
                                      });
                                    }}
                                    _hover={{
                                      bg: 'red.600',
                                      transform: 'scale(1.05)',
                                    }}
                                    transition="all 0.2s ease-in-out"
                                    title="Remove from market"
                                  />
                                </HStack>
                              )}

                              {/* Practice Content */}
                              <VStack align="stretch" spacing="1rem">
                                {/* Header */}
                                <Box>
                                  <Text
                                    fontSize="0.875rem"
                                    fontWeight="600"
                                    color="gray.600"
                                    mb="0.5rem"
                                  >
                                    {practice.market || 'No Market'}
                                  </Text>
                                  <Text
                                    fontSize="1.5rem"
                                    fontWeight="700"
                                    color="text.primary"
                                    _hover={{
                                      color: 'text.brandLight',
                                    }}
                                    cursor="pointer"
                                  >
                                    {practice.name}
                                  </Text>
                                </Box>

                                {/* Contact Info */}
                                <HStack spacing="2rem" align="start">
                                  <VStack
                                    align="start"
                                    spacing="0.5rem"
                                    flex={1}
                                  >
                                    {getCustomFieldValue(
                                      practice.customFields,
                                      'Backline'
                                    ) && (
                                      <CustomFieldBadge
                                        customField="Backline"
                                        fieldValue={getCustomFieldValue(
                                          practice.customFields,
                                          'Backline'
                                        )}
                                        bgColor="#FED7D7"
                                        titleColor="#9B2C2C"
                                        valueColor="#9B2C2C"
                                      />
                                    )}
                                    {getCustomFieldValue(
                                      practice.customFields,
                                      'Escalation'
                                    ) && (
                                      <CustomFieldBadge
                                        customField="Escalation"
                                        fieldValue={getCustomFieldValue(
                                          practice.customFields,
                                          'Escalation'
                                        )}
                                        bgColor="background.yellow100"
                                      />
                                    )}
                                    {getCustomFieldValue(
                                      practice.customFields,
                                      'Phone'
                                    ) && (
                                      <Text
                                        fontSize="0.875rem"
                                        color="text.secondary"
                                      >
                                        Phone{' '}
                                        {getCustomFieldValue(
                                          practice.customFields,
                                          'Phone'
                                        )}
                                      </Text>
                                    )}
                                    {getCustomFieldValue(
                                      practice.customFields,
                                      'Fax'
                                    ) && (
                                      <Text
                                        fontSize="0.875rem"
                                        color="text.secondary"
                                      >
                                        Fax{' '}
                                        {getCustomFieldValue(
                                          practice.customFields,
                                          'Fax'
                                        )}
                                      </Text>
                                    )}
                                  </VStack>

                                  <VStack
                                    align="start"
                                    spacing="0.5rem"
                                    flex={1}
                                  >
                                    <Text
                                      fontSize="0.875rem"
                                      color="text.secondary"
                                    >
                                      {practice.address1}
                                      {practice.city &&
                                        `, ${practice.city}`}
                                      {practice.state &&
                                        `, ${practice.state}`}
                                      {practice.zip &&
                                        ` ${practice.zip}`}
                                    </Text>
                                    {getCustomFieldValue(
                                      practice.customFields,
                                      'Manager-Email'
                                    ) && (
                                      <Text
                                        fontSize="0.875rem"
                                        color="text.secondary"
                                        noOfLines={1}
                                      >
                                        {getCustomFieldValue(
                                          practice.customFields,
                                          'Manager-Email'
                                        )}
                                      </Text>
                                    )}
                                  </VStack>
                                </HStack>
                              </VStack>
                            </Box>
                          </HStack>
                        ))}
                      </VStack>
                    )}
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            </Box>

            {/* Search Results */}
            {searchQuery && (
              <Box>
                <Accordion
                  allowToggle
                  index={
                    expandedAccordion === 'search-results' ? 0 : -1
                  }
                  onChange={(index) =>
                    setExpandedAccordion(
                      index === 0 ? 'search-results' : ''
                    )
                  }
                >
                  <AccordionItem
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                  >
                    <AccordionButton>
                      <Box flex="1" textAlign="left">
                        <HStack spacing="1rem">
                          <Badge
                            colorScheme="green"
                            variant="subtle"
                            fontSize="0.75rem"
                          >
                            Step 3
                          </Badge>
                          <Text fontSize="1rem" fontWeight="600">
                            Search Results for "{searchQuery}"
                          </Text>
                          <Badge
                            colorScheme={
                              searchResults.length > 0
                                ? 'green'
                                : 'gray'
                            }
                            variant="solid"
                            fontSize="0.7rem"
                          >
                            {searchResults.length} result
                            {searchResults.length !== 1 ? 's' : ''}
                          </Badge>
                          {searchResults.length > 0 && (
                            <Badge
                              colorScheme="blue"
                              variant="outline"
                              fontSize="0.65rem"
                              animation="pulse"
                            >
                              Click to view
                            </Badge>
                          )}
                        </HStack>
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel pb={4}>
                      <HStack
                        spacing="1rem"
                        mb="1rem"
                        justify="flex-end"
                      >
                        <Text fontSize="0.875rem" color="gray.500">
                          {searchLoading
                            ? 'Searching...'
                            : 'Ready to add'}
                        </Text>
                      </HStack>

                      {searchResults.length > 0 && (
                        <Alert
                          status="success"
                          borderRadius="md"
                          mb="1rem"
                        >
                          <AlertIcon />
                          <Box>
                            <AlertTitle
                              fontSize="0.75rem"
                              mb="0.25rem"
                            >
                              Ready to add practices?
                            </AlertTitle>
                            <AlertDescription fontSize="0.7rem">
                              Select practices using the checkboxes,
                              then use the toolbar at the bottom to
                              add them to this market
                            </AlertDescription>
                          </Box>
                        </Alert>
                      )}
                      {searchLoading ? (
                        <Flex
                          justify="center"
                          align="center"
                          py="3rem"
                          bg="blue.50"
                          borderRadius="md"
                          border="1px solid"
                          borderColor="blue.200"
                        >
                          <VStack spacing="1rem">
                            <Spinner
                              size="lg"
                              color="blue.500"
                              thickness="4px"
                            />
                            <Text
                              color="blue.600"
                              fontSize="0.875rem"
                            >
                              Searching practices...
                            </Text>
                          </VStack>
                        </Flex>
                      ) : searchResults.length === 0 ? (
                        <Flex
                          justify="center"
                          align="center"
                          py="3rem"
                          bg="gray.50"
                          borderRadius="md"
                          border="1px solid"
                          borderColor="gray.200"
                        >
                          <VStack spacing="1rem">
                            <Text
                              color="gray.500"
                              fontSize="1rem"
                              fontWeight="500"
                            >
                              No practices found
                            </Text>
                            <Text
                              color="gray.400"
                              fontSize="0.875rem"
                              textAlign="center"
                            >
                              Try adjusting your search terms or check
                              the spelling
                            </Text>
                          </VStack>
                        </Flex>
                      ) : (
                        <VStack spacing="1rem" align="stretch">
                          {searchResults.map((practice) => (
                            <HStack
                              key={practice.id}
                              spacing="1rem"
                              align="start"
                            >
                              {/* Checkbox - Outside the card */}
                              {(isAdmin() || canEditInsurance()) && (
                                <Checkbox
                                  isChecked={selectedIds.has(
                                    practice.id
                                  )}
                                  onChange={() =>
                                    toggleSelection(practice.id)
                                  }
                                  colorScheme="blue"
                                  mt="0.5rem"
                                />
                              )}

                              {/* Practice Card */}
                              <Box
                                flex="1"
                                p="1rem"
                                bg="white"
                                border="1px solid"
                                borderColor="border.subtle"
                                borderRadius="0.5rem"
                                transition="all 0.2s ease-in-out"
                                _hover={{
                                  transform: 'translateY(-1px)',
                                  boxShadow: 'md',
                                  borderColor: practice.isInMarket
                                    ? 'green.200'
                                    : 'blue.200',
                                }}
                                cursor="pointer"
                                onClick={() =>
                                  window.open(
                                    `/practices/${practice.id}`,
                                    '_blank'
                                  )
                                }
                              >
                                <VStack
                                  align="stretch"
                                  spacing="0.75rem"
                                >
                                  {/* Practice Name */}
                                  <Text
                                    fontSize="1.25rem"
                                    fontWeight="700"
                                    color="text.primary"
                                    lineHeight="1.4"
                                    noOfLines={2}
                                    cursor="pointer"
                                    _hover={{
                                      color: 'text.brandLight',
                                    }}
                                  >
                                    {practice.name}
                                  </Text>

                                  {/* Practice Details */}
                                  <VStack
                                    align="stretch"
                                    spacing="0.5rem"
                                  >
                                    {practice.address1 && (
                                      <Text
                                        fontSize="0.875rem"
                                        color="text.secondary"
                                        noOfLines={1}
                                      >
                                        {practice.address1}
                                      </Text>
                                    )}
                                    {practice.department && (
                                      <Text
                                        fontSize="0.875rem"
                                        color="text.secondary"
                                        noOfLines={1}
                                      >
                                        {practice.department}
                                      </Text>
                                    )}
                                    {practice.taxId && (
                                      <Text
                                        fontSize="0.875rem"
                                        color="text.secondary"
                                        noOfLines={1}
                                      >
                                        {practice.taxId}
                                      </Text>
                                    )}
                                  </VStack>

                                  {/* Status */}
                                  <VStack
                                    align="stretch"
                                    spacing="0.75rem"
                                    mt="0.5rem"
                                  >
                                    {practice.isInMarket ? (
                                      <Box
                                        p="0.5rem"
                                        bg="green.50"
                                        borderRadius="md"
                                        border="1px solid"
                                        borderColor="green.200"
                                        textAlign="center"
                                      >
                                        <Text
                                          fontSize="0.875rem"
                                          color="green.700"
                                          fontWeight="500"
                                        >
                                          Already in this market
                                        </Text>
                                      </Box>
                                    ) : (
                                      <Box
                                        p="0.5rem"
                                        bg="blue.50"
                                        borderRadius="md"
                                        border="1px solid"
                                        borderColor="blue.200"
                                        textAlign="center"
                                      >
                                        <Text
                                          fontSize="0.875rem"
                                          color="blue.700"
                                          fontWeight="500"
                                        >
                                          Available to add
                                        </Text>
                                      </Box>
                                    )}
                                  </VStack>
                                </VStack>
                              </Box>
                            </HStack>
                          ))}
                        </VStack>
                      )}
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>
              </Box>
            )}
          </Box>
        </Flex>
      </Box>

      {/* Sticky Bulk Toolbar */}
      {selectedIds.size > 0 && (isAdmin() || canEditInsurance()) && (
        <Flex
          position="fixed"
          bottom="0"
          left="0"
          right="0"
          bg="white"
          borderTop="2px solid"
          borderColor="blue.200"
          p="1rem"
          boxShadow="xl"
          zIndex={1000}
          justify="center"
        >
          <VStack spacing="0.75rem" maxW="1200px" width="100%">
            {/* Progress Bar */}
            {bulkProgress && (
              <Box width="100%">
                <HStack justify="space-between" mb="0.5rem">
                  <Text
                    fontSize="0.75rem"
                    color="gray.600"
                    fontWeight="500"
                  >
                    {bulkProgress.current}
                  </Text>
                  <Text fontSize="0.75rem" color="gray.600">
                    {bulkProgress.completed} of {bulkProgress.total}
                  </Text>
                </HStack>
                <Progress
                  value={
                    (bulkProgress.completed / bulkProgress.total) *
                    100
                  }
                  colorScheme="blue"
                  size="sm"
                  borderRadius="md"
                />
              </Box>
            )}

            <HStack spacing="1rem" width="100%">
              <Badge
                colorScheme="blue"
                variant="solid"
                fontSize="0.7rem"
              >
                {selectedIds.size} selected
              </Badge>
              <Text
                fontSize="0.875rem"
                fontWeight="600"
                color="gray.700"
              >
                Bulk Actions Available
              </Text>

              <Button
                size="sm"
                colorScheme="green"
                onClick={() => {
                  const searchIds = Array.from(selectedIds).filter(
                    (id) =>
                      searchResults.some(
                        (p) => p.id === id && !p.isInMarket
                      )
                  );
                  if (searchIds.length > 0) {
                    bulkAdd(searchIds);
                  }
                }}
                isDisabled={
                  bulkBusy === 'add' ||
                  !Array.from(selectedIds).some((id) =>
                    searchResults.some(
                      (p) => p.id === id && !p.isInMarket
                    )
                  )
                }
                isLoading={bulkBusy === 'add'}
              >
                Add to Market
              </Button>

              <Button
                size="sm"
                colorScheme="red"
                onClick={() => {
                  const marketIds = Array.from(selectedIds).filter(
                    (id) => marketPractices.some((p) => p.id === id)
                  );
                  setBulkRemoveCount(marketIds.length);
                  setBulkRemoveTargetMarket('');
                  bulkRemoveModal.onOpen();
                }}
                isDisabled={
                  bulkBusy === 'remove' ||
                  !Array.from(selectedIds).some((id) =>
                    marketPractices.some((p) => p.id === id)
                  )
                }
                isLoading={bulkBusy === 'remove'}
              >
                Move to Market
              </Button>

              <Divider orientation="vertical" height="2rem" />

              <Button
                size="sm"
                variant="outline"
                onClick={clearSelection}
                isDisabled={!!bulkBusy}
              >
                Clear Selection
              </Button>

              <Text fontSize="0.75rem" color="gray.500" ml="auto">
                Use bulk actions to add/remove multiple practices at
                once
              </Text>
            </HStack>
          </VStack>
        </Flex>
      )}

      {/* Remove Practice Modal */}
      <Modal
        isOpen={removeModal.isOpen}
        onClose={removeModal.onClose}
        size="md"
      >
        <ModalOverlay
          bg="blackAlpha.600"
          backdropFilter="blur(4px)"
        />
        <ModalContent borderRadius="lg" boxShadow="xl">
          <ModalHeader
            borderBottom="1px solid"
            borderColor="gray.200"
            pb="1rem"
            fontSize="1.125rem"
            fontWeight="600"
          >
            Move Practice to Another Market
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py="1.5rem">
            <VStack spacing="1.5rem" align="stretch">
              <Box
                p="1rem"
                bg="red.50"
                borderRadius="md"
                border="1px solid"
                borderColor="red.200"
              >
                <Text
                  fontSize="0.875rem"
                  color="red.700"
                  fontWeight="500"
                >
                  You are moving{' '}
                  <strong>{selectedPractice?.name}</strong> from this
                  market to another market.
                </Text>
              </Box>

              <Text fontSize="0.875rem" color="gray.600">
                To which market should this practice be assigned?
              </Text>
              <Text
                fontSize="0.75rem"
                color="blue.600"
                fontWeight="500"
              >
                Choose a different market to move this practice to
              </Text>

              <FormControl>
                <FormLabel
                  fontSize="0.875rem"
                  fontWeight="500"
                  color="gray.700"
                >
                  Select Market
                </FormLabel>
                <Select
                  placeholder="Choose a market..."
                  value={selectedMarket}
                  onChange={(e) => setSelectedMarket(e.target.value)}
                  bg="white"
                  _focus={{
                    borderColor: 'blue.500',
                    boxShadow: '0 0 0 1px #3182ce',
                  }}
                >
                  {marketOptions
                    .filter((option) => option.value !== marketId) // Exclude current market
                    .map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter
            borderTop="1px solid"
            borderColor="gray.200"
            pt="1rem"
            gap="0.75rem"
          >
            <Button
              variant="ghost"
              onClick={removeModal.onClose}
              _hover={{ bg: 'gray.100' }}
              transition="all 0.2s ease-in-out"
            >
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={removePracticeFromMarket}
              isLoading={busyIds.has(selectedPractice?.id || '')}
              loadingText="Moving..."
              isDisabled={!selectedMarket}
              _hover={{ transform: 'translateY(-1px)' }}
              transition="all 0.2s ease-in-out"
            >
              {busyIds.has(selectedPractice?.id || '')
                ? 'Moving...'
                : 'Move Practice'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Bulk Remove Practice Modal */}
      <Modal
        isOpen={bulkRemoveModal.isOpen}
        onClose={bulkRemoveModal.onClose}
        size="md"
      >
        <ModalOverlay
          bg="blackAlpha.300"
          backdropFilter="blur(10px) hue-rotate(90deg)"
        />
        <ModalContent
          borderRadius="0.75rem"
          boxShadow="0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
          border="1px solid"
          borderColor="gray.200"
        >
          <ModalHeader
            fontSize="1.25rem"
            fontWeight="700"
            color="text.primary"
            borderBottom="1px solid"
            borderColor="gray.100"
            pb="1rem"
          >
            Move {bulkRemoveCount}{' '}
            {bulkRemoveCount === 1 ? 'Practice' : 'Practices'} to
            Another Market
          </ModalHeader>
          <ModalCloseButton
            size="lg"
            _hover={{ bg: 'gray.100' }}
            transition="all 0.2s ease-in-out"
          />
          <ModalBody py="1.5rem">
            <Box
              p="1rem"
              bg="red.50"
              borderRadius="0.5rem"
              border="1px solid"
              borderColor="red.200"
              mb="1.5rem"
            >
              <Text
                fontSize="0.875rem"
                color="red.700"
                fontWeight="500"
              >
                ⚠️ You are moving {bulkRemoveCount}{' '}
                {bulkRemoveCount === 1 ? 'practice' : 'practices'}{' '}
                from this market to another market.
              </Text>
            </Box>

            <FormControl isRequired>
              <FormLabel
                fontSize="0.875rem"
                fontWeight="600"
                color="text.primary"
                mb="0.5rem"
              >
                Select Target Market
              </FormLabel>
              <Select
                placeholder="Choose a market..."
                value={bulkRemoveTargetMarket}
                onChange={(e) =>
                  setBulkRemoveTargetMarket(e.target.value)
                }
                bg="white"
                _focus={{
                  boxShadow: '0 0 0 1px #3182ce',
                  borderColor: '#3182ce',
                }}
                borderColor="gray.300"
                _hover={{ borderColor: 'gray.400' }}
                transition="all 0.2s ease-in-out"
              >
                {marketOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </FormControl>
          </ModalBody>
          <ModalFooter
            gap="0.75rem"
            borderTop="1px solid"
            borderColor="gray.100"
            pt="1rem"
          >
            <Button
              variant="ghost"
              onClick={() => {
                bulkRemoveModal.onClose();
                setBulkRemoveCount(0);
              }}
              _hover={{ bg: 'gray.100' }}
              transition="all 0.2s ease-in-out"
            >
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleBulkRemove}
              isLoading={bulkBusy === 'remove'}
              loadingText="Removing..."
              isDisabled={!bulkRemoveTargetMarket}
              _hover={{ transform: 'translateY(-1px)' }}
              transition="all 0.2s ease-in-out"
            >
              {bulkBusy === 'remove'
                ? 'Moving...'
                : `Move ${bulkRemoveCount} Practice${
                    bulkRemoveCount === 1 ? '' : 's'
                  }`}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default MarketPracticeManagement;